import * as React from 'react';
import * as ReactMarkdown from 'react-markdown';
import { withTheme } from 'styled-components';

import { AboutRenderer, IRenderProps } from '../../models/About';
import styled, { tstyled, ITheme } from '../../theme';

const Container = styled.div`
  padding-bottom: 75px;

  @media (max-width: ${(props) => props.theme.maxWidth}) {
    > * {
      &:first-child {
        padding-left: 40px;
      }

      &:last-child {
        padding-right: 40px;
      }
    }
  }

  @media (max-width: ${(props) => props.theme.aboutBreakpoint - 1}px) {
    padding: ${(props) => props.theme.mobilePadding}px;

    > * {
      &:first-child {
        padding-left: 0;
      }

      &:last-child {
        padding-right: 0;
      }

      margin-bottom: ${(props) => props.theme.mobilePadding}px;

      &:last-child {
        margin-bottom: 0;
      }
    }
  }

  @media (min-width: ${(props) => props.theme.aboutBreakpoint}px) {
    margin-top: 75px;
    display: flex;
    justify-content: space-between;

    > * {
      width: 48%;
    }
  }

  a {
    color: inherit;
    &:hover {
      transform: scaleY(-1);
    }
  }

  p {
    margin-bottom: 25px;
    line-height: 1.5;

    @media (min-width: 1800px) {
      font-size: 0.8vw;
    }
  }
`;

const ImageComponent = tstyled<
  { visible: boolean; loaded: boolean },
  HTMLDivElement
>(styled.div)`
  background-blend-mode: multiply;
  background-color: #af6c2a;
  background-size: cover;
  background-position: center center;
  transition: 0.53s ease-in-out filter, 0.4s ease opacity;
  will-change: filter;
  height: 400px;
  opacity: ${(props) => (props.loaded ? 1 : 0)}

  @media (min-width: ${(props) => props.theme.aboutBreakpoint}px) {
    height: 100%;
    width: 100%;
  }
`;

interface IState {
  imageHeight: number;
  loaded: boolean;
  visible: boolean;
  isMobile: boolean;
}

type IProps = IRenderProps & {
  theme: ITheme;
};

const ImageArea = withTheme(
  class extends React.PureComponent<IProps, IState> {
    state: IState = {
      imageHeight: 0,
      loaded: false,
      visible: false,
      isMobile: false,
    };

    ratio: number;
    imageContainer: HTMLDivElement;

    constructor(props: IProps) {
      super(props);
      this.ratio =
        this.props.data.fields.image.fields.file.details.image.width /
        this.props.data.fields.image.fields.file.details.image.height;
    }

    componentDidMount() {
      window.addEventListener('resize', this.handleResize);
      this.preloadImage();
      this.handleResize();
    }

    determineMobile = () => {
      const shouldBeMobile =
        window.innerWidth < this.props.theme.aboutBreakpoint;
      if (this.state.isMobile !== shouldBeMobile) {
        this.setState({ isMobile: shouldBeMobile });
      }
    };

    preloadImage() {
      const img = new Image();
      img.addEventListener('load', () => {
        this.setState({ loaded: true });
      });
      img.src = this.props.data.fields.image.fields.file.url;
    }

    componentWillUnmount() {
      window.removeEventListener('resize', this.handleResize);
    }

    handleResize = () => {
      requestAnimationFrame(this.sizeImage);
      requestAnimationFrame(this.determineMobile);
    };

    sizeImage = () => {
      this.setState({
        imageHeight: this.imageContainer.clientWidth / this.ratio,
      });
    };

    onMouseEnter = () => {
      this.setState({ visible: true });
    };

    onMouseLeave = () => {
      this.setState({ visible: false });
    };

    toggleVisible = () => {
      this.setState((prevState) => ({
        visible: !prevState.visible,
      }));
    };

    render() {
      const { isMobile, loaded } = this.state;

      return (
        <Container>
          <div>
            <ImageComponent
              visible={this.state.visible}
              innerRef={(ref) => (this.imageContainer = ref)}
              onMouseEnter={isMobile ? undefined : this.onMouseEnter}
              onMouseLeave={isMobile ? undefined : this.onMouseLeave}
              onClick={isMobile ? this.toggleVisible : undefined}
              loaded={loaded}
              style={{
                height: this.state.imageHeight,
                backgroundImage: loaded
                  ? `url(${this.props.data.fields.image.fields.file.url})`
                  : undefined,
              }}
            />
          </div>
          <div>
            <ReactMarkdown
              source={this.props.data.fields.description}
              renderers={{
                link: (p) => {
                  if (!p.href.startsWith('http')) {
                    return p.href;
                  }

                  return (
                    <a
                      href={p.href}
                      rel="nofollow noreferrer noopener"
                      target="_blank"
                    >
                      {p.children}
                    </a>
                  );
                },
              }}
            />
          </div>
        </Container>
      );
    }
  }
);

function Wrapper() {
  return <AboutRenderer render={ImageArea} />;
}

export default Wrapper;



// WEBPACK FOOTER //
// ./src/scenes/About/ImageArea.tsx